/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.14.0
 */

import type { ReactElement } from 'react';

import { FieldProps, useField, useFormikContext } from 'formik';
import get from 'lodash/get';

import { FormFieldStyled } from '@components/common/FormStyled/FormStyled';
import { EInputKind } from '@components/common/Input/Input';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';

export interface IFormFieldChildrenProps extends Pick<FieldProps, 'field'>, Omit<IFormFieldProps, 'children' | 'name'> {
    kind: EInputKind;
    label: string;
}

export interface IFormFieldChildren {
    children: (props: IFormFieldChildrenProps) => ReactElement;
}

export interface IFormFieldProps {
    dataName: string;
    disabled?: boolean;
    errorKey?: string; // Ключ для получение текста ошибка (для вложенных объектов)
    hideValidation?: boolean;
    label?: string;
    labelHint?: ReactElement;
    name: string;
    placeholder?: string;
    inputtype?: string;
    required?: boolean;
    hintMessage?: string;
}

const FormField = ({
    children,
    errorKey,
    hideValidation,
    label,
    name,
    required,
    ...props
}: IFormFieldProps & IFormFieldChildren) => {
    const [field] = useField({ name });
    const { errors, touched, dirty } = useFormikContext();

    if (!field || !children) {
        return <></>;
    }

    const error: string = get(touched, field.name) || dirty ? get(errors, errorKey || field.name, '') : '';
    const errorMsg: string = !hideValidation ? error.toString() : '';

    return (
        <FormFieldStyled kind={error ? EInputKind.error : EInputKind.normal}>
            <ArrowTooltip placement="top" title={errorMsg} titleAlign="center">
                <div>
                    {children({
                        ...props,
                        field,
                        kind: error ? EInputKind.error : EInputKind.normal,
                        label: label?.toString() || '',
                        required: required || false,
                    })}
                </div>
            </ArrowTooltip>
        </FormFieldStyled>
    );
};

export default FormField;
